import React, { useState, useRef } from "react";
import "./contentpage.css";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";
import Question from "../components/Question";
import BadgePopup from "../components/Badgepopup";

function ContemporaryFinance60() {
  const [isOpen, setIsOpen] = useState(true);
  const contentRef = useRef(null);
  const titleText = "Altcoins are Fiat in Disguise";
  const links = {
    prev: "/contemporaryfinance-59",
    index: "/index-finance",
    next: "/index-finance",
  };
  const questions = [
    {
      text: "1. What is the central claim in the lesson about altcoins in relation to the fiat money system?",
      options: [
        {
          value: "A",
          label: "A. They completely eliminate fiat’s flaws",
        },
        {
          value: "B",
          label: "B. They are a true form of decentralization",
        },
        {
          value: "C",
          label: "C. They replicate the same flaws of fiat in digital form",
        },
        {
          value: "D",
          label: "D. They have no similarities with modern banking",
        },
      ],
      correct: "C",
    },
    {
      text: "2. Which feature most clearly distinguishes Bitcoin from altcoins?",
      options: [
        {
          value: "A",
          label: "A. Backing by central institutions",
        },
        {
          value: "B",
          label: "B. Unlimited issuance potential",
        },
        {
          value: "C",
          label: "C. Fixed supply and decentralization",
        },
        {
          value: "D",
          label: "D. Support from banking systems",
        },
      ],
      correct: "C",
    },
    {
      text: "3. Why are many altcoins compared to the modern banking system?",
      options: [
        {
          value: "A",
          label: "A. They rely solely on physical notes",
        },
        {
          value: "B",
          label: "B. They use identical interest rate models",
        },
        {
          value: "C",
          label: "C. They enrich insiders at the expense of the majority",
        },
        {
          value: "D",
          label: "D. They are designed with no profit motive",
        },
      ],
      correct: "C",
    },
    {
      text: "4. What is the primary intent behind the creation of most altcoins?",
      options: [
        {
          value: "A",
          label: "A. To replace fiat with a stronger monetary system",
        },
        {
          value: "B",
          label: "B. To enrich their issuers through centralized control",
        },
        {
          value: "C",
          label: "C. To maintain strict decentralization",
        },
        {
          value: "D",
          label: "D. To remove speculation from markets",
        },
      ],
      correct: "B",
    },
  ];

  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);

  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={60}
        timerMinutes={60}
        currentLevel={4}
        unlockedBadges={7}
      />

      <p className="content">
        It is essential to include this chapter in the broader discussion about
        the fiat monetary system to ensure that people do not get deceived into
        thinking that altcoins are any different from the modern banking system.
        While Bitcoin remains the only truly decentralized and
        censorship-resistant cryptocurrency, most other cryptocurrencies,
        commonly known as altcoins, are merely a reflection of the same flaws
        that plague fiat currencies and the central banking system. Many altcoin
        enthusiasts claim that cryptocurrencies, as a whole, provide a viable
        alternative to the fiat ecosystem, offering freedom from centralized
        control and inflationary practices. However, when you look deeper into
        the mechanics of altcoins, it becomes clear that they are not a solution
        to the fiat system but rather another version of it. This lesson will
        explore why altcoins are often no better than fiat money, how they are
        centralized and subject to inflationary pressures, and why they are
        fundamentally flawed in the same way that the modern banking system is.
        <br />
        <br />
        <b>What Are Altcoins? A Brief Overview</b>
        <br />
        <br />
        Altcoins, short for "alternative coins," are cryptocurrencies other than
        Bitcoin. Since the creation of Bitcoin in 2009, many other
        cryptocurrencies have emerged, each with their own unique features,
        purposes, and technological underpinnings. The most well-known altcoins
        include Ethereum, Ripple (XRP), Litecoin, Cardano, and Polkadot, to name
        a few. These coins typically claim to offer improvements or alternatives
        to Bitcoin's blockchain technology, with promises of faster
        transactions, lower fees, or more flexible smart contract capabilities.
        However, while they may introduce technical innovations or marketing
        strategies that set them apart from Bitcoin, altcoins generally fall
        short of providing the core features that make Bitcoin unique, namely
        decentralization, immutability, and a fixed supply.
        <br />
        Altcoins often rely on centralized models of control. Unlike Bitcoin,
        which is governed by an open-source, decentralized network of miners,
        node-runners, developers, and other users altcoins often have central
        entities behind them. For example, many altcoins are created and
        maintained by development teams or companies that can influence
        decisions on protocol upgrades, token issuance, and network governance.
        In some cases, these entities hold large amounts of the coin supply,
        giving them disproportionate control over the network and its future
        direction. Furthermore, many altcoins are marketed with promises of huge
        returns, creating speculative markets where prices are manipulated by
        the central entities or groups controlling the coins. In essence,
        altcoins are not fundamentally different from fiat currencies in that
        they are created and controlled by a central authority, often for the
        benefit of that authority, rather than serving as a truly decentralized
        store of value.
        <br />
        <br />
        <b>Centralization and Inflation in Altcoins</b>
        <br />
        <br />
        One of the core tenets of Bitcoin is its decentralization, meaning no
        single entity has control over the network. Bitcoin operates on a
        decentralized peer-to-peer network where all transactions are verified
        by participants in the network, and the issuance of new coins is limited
        and predetermined by the code. Altcoins, however, often fail to provide
        this level of decentralization. Many altcoins are built on networks
        where a small group of developers or founders have significant control
        over key decisions. This centralization gives these parties the power to
        alter the coin’s code, adjust the monetary policy, or decide who can or
        cannot participate in the network.
        <br />
        Moreover, many altcoins are created with inflationary mechanisms that
        allow for an increase in the supply of coins over time. This
        inflationary process is often touted as a way to incentivize network
        participants, such as miners or stakers, but it mirrors the inflationary
        practices of fiat currencies. In a fiat system, central banks print
        money at will, diluting the value of existing currency. Similarly, many
        altcoins issue new coins to reward network participants or fund
        projects, leading to inflation and the potential for devaluation of the
        currency. This is contrary to the fixed supply model of Bitcoin, where
        only 21 million coins will ever be created, ensuring that inflation does
        not undermine its value over time.
        <br />
        By allowing for inflationary issuance, altcoins perpetuate the same
        problem found in fiat money: the debasement of currency over time. This
        inflationary tendency is built into the DNA of most altcoins, yet it is
        often overlooked or downplayed by their proponents. Just as fiat
        currencies erode the purchasing power of citizens, altcoins that are
        subject to inflationary pressures diminish their value, making them
        unreliable stores of value. Rather than offering an alternative to the
        fiat system, they perpetuate the very problem Bitcoin was designed to
        solve.
        <br />
        <br />
        <b>The Centralization of Wealth and Power in Altcoins</b>
        <br />
        <br />
        Another disturbing similarity between altcoins and the fiat system is
        the centralization of wealth and power. In the fiat world, a small group
        of central bankers and government officials control the issuance of
        money, leading to wealth inequality and financial instability. Altcoins
        often replicate this model by allowing a small group of early adopters,
        developers, or investors to hold a disproportionate share of the coin
        supply. These "whales" can manipulate the price of the coin and control
        the direction of the project.
        <br />
        For example, many altcoins are initially distributed through initial
        coin offerings (ICOs) or pre-mines, where a large portion of the coin
        supply is sold to early investors or kept by the development team. This
        allows those with insider knowledge and capital to accumulate vast
        amounts of the coin, leaving little opportunity for the average
        participant to gain a meaningful stake. As a result, the wealth
        distribution within altcoin ecosystems is often highly skewed, just as
        it is in fiat economies.
        <br />
        This centralization of wealth and power in altcoin ecosystems is
        problematic because it creates incentives for the insiders to manipulate
        the market for their benefit. When the value of the coin increases, the
        wealth of the insiders grows exponentially. This dynamic is strikingly
        similar to how fiat money printing benefits the wealthy and
        well-connected, as they are the first to receive the new currency and
        are able to use it before inflation sets in. The average user, who buys
        into the altcoin at a later stage, is left with an inflated currency
        whose value has already been diluted. Thus, rather than creating a
        fairer, decentralized alternative to fiat money, altcoins often
        perpetuate the same wealth inequality that exists in the fiat financial
        system.
        <br />
        <br />
        <b>The Illusion of Altcoins as a Solution to Fiat</b>
        <br />
        <br />
        Despite these flaws, many altcoin advocates argue that these
        cryptocurrencies are a solution to the problems posed by the fiat
        system. They claim that altcoins provide greater freedom, faster
        transactions, and lower fees than traditional fiat currencies or even
        Bitcoin. Some altcoins also tout features such as smart contracts,
        decentralized applications, and enhanced privacy, which they argue make
        them superior to Bitcoin and fiat money. However, this view ignores the
        fundamental issue that altcoins, like fiat currencies, are often
        controlled by centralized entities and can be subject to inflationary
        pressures. While they may offer certain technical improvements or
        features, these do not address the core problem that most altcoins still
        rely on centralization and can be manipulated by those who control the
        network.
        <br />
        In fact, altcoins are not a solution to the fiat monetary system but a
        continuation of it. Just as central banks have the power to print money
        and devalue its purchasing power, altcoin creators and early investors
        can manipulate the supply and demand of their coins to benefit their own
        interests. Even when altcoins offer unique features, they still lack the
        decentralization and fixed supply that make Bitcoin a truly alternative
        monetary system. Without these core principles, altcoins remain
        vulnerable to inflation, centralization, and manipulation, making them
        unsuitable as a long-term solution to fiat money's inherent flaws.
        <br />
        While altcoins may seem like an attractive alternative to the
        traditional fiat banking system, they are ultimately flawed in the same
        ways that fiat currencies are. Their centralization, potential for
        inflation, and the concentration of power in the hands of a few insiders
        make them no better than fiat money. Only Bitcoin, with its
        decentralized nature, fixed supply, and resistance to manipulation,
        offers a true alternative to the fiat monetary system. Understanding the
        similarities between altcoins and fiat money is essential for anyone
        seeking a way out of the current monetary system, as it helps to avoid
        the illusion that altcoins can offer a meaningful solution to the
        problems caused by central banking and fiat currencies.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        Today we learned how altcoins present themselves as innovative solutions
        to the problems of the fiat system, but in reality they replicate the
        same flaws under a different label. While advocates claim that these
        tokens bring decentralization, choice, and technological progress, most
        are centrally issued, easily manipulated, and designed primarily to
        enrich their creators. Unlike Bitcoin, altcoins often lack hard supply
        limits, can be inflated at will, and operate in structures that closely
        mirror the banking system where insiders benefit at the expense of the
        majority. This makes them not a path away from fiat dependency but an
        extension of it in digital form. Only Bitcoin, with its fixed supply,
        decentralized nature, and incorruptible design, stands apart as a
        genuine alternative.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>
      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
      <BadgePopup
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        level="Level 4"
        badgeImg="./achieve.png"
        heading="Monetary Policy Archtect"
        subtitle="Graduate - Review of Comtemporary Financial Institutes"
        mainImg="./monetarypolicyarchitect.png"
        description="Honors your Monetary Intelligence. You have mastered the arguments against monetary debasement and possess the knowledge to audit central bank policies, advocate for sound money, and protect wealth from inflationary decay."
        buttonText="Continue!"
      />
    </div>
  );
}

export default ContemporaryFinance60;
